import { supabase } from '../supabaseClient';
import type { ClientContact, ContactType } from '@shared/types';

// Helper to transform database row to ClientContact object
function transformContact(row: any): ClientContact {
  return {
    id: row.id,
    clientId: row.client_id,
    name: row.name,
    email: row.email,
    phone: row.phone,
    jobTitle: row.job_title,
    contactType: row.contact_type,
    isPrimary: row.is_primary,
    notes: row.notes,
    externalSource: row.external_source,
    externalId: row.external_id,
    externalData: row.external_data,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export const clientContactService = {
  async getClientContacts(clientId: string) {
    const { data, error } = await supabase
      .from('client_contacts')
      .select('*')
      .eq('client_id', clientId)
      .order('is_primary', { ascending: false })
      .order('name', { ascending: true});
    
    if (error) throw error;

    return data ? data.map(transformContact) : [];
  },

  async getContact(id: string) {
    const { data, error } = await supabase
      .from('client_contacts')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return transformContact(data);
  },

  async createContact(contact: Partial<ClientContact>) {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('client_contacts')
      .insert([{
        client_id: contact.clientId,
        name: contact.name,
        email: contact.email || null,
        phone: contact.phone || null,
        job_title: contact.jobTitle || null,
        contact_type: contact.contactType,
        is_primary: contact.isPrimary || false,
        notes: contact.notes || null,
        created_by: user.id,
      }])
      .select()
      .single();
    
    if (error) {
      console.error('Create contact error:', error);
      throw error;
    }
    
    return transformContact(data);
  },

  async updateContact(id: string, updates: Partial<ClientContact>) {
    const { data, error } = await supabase
      .from('client_contacts')
      .update({
        name: updates.name,
        email: updates.email || null,
        phone: updates.phone || null,
        job_title: updates.jobTitle || null,
        contact_type: updates.contactType,
        is_primary: updates.isPrimary,
        notes: updates.notes || null,
      })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return transformContact(data);
  },

  async deleteContact(id: string) {
    const { error } = await supabase
      .from('client_contacts')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  async setPrimaryContact(clientId: string, contactId: string) {
    // The trigger will automatically unset other primary contacts
    return this.updateContact(contactId, { isPrimary: true });
  },
};

